import { EntryFromSkeleton } from '@tuic/contentful';
import { TypeAlertSkeleton } from '@tuic/contentful/generated/dashboard';
import { getComparisonKey } from './serialization';
import { StoredDismissedAlert } from './types';
import { reduceToDismissDateAndIndexByKey } from './utils';
import { DateTime } from 'luxon';

/**
 * Creates a filter function that checks if the provided entry is dismissed based on the dismissed alerts.
 */
export const createServerAlertFilter = (args: {
  origin: string;
  dismissedAlerts: StoredDismissedAlert[];
}) => {
  let { origin, dismissedAlerts } = args;

  // Group dismissed alerts by their dismissed date
  let groupedByDate = reduceToDismissDateAndIndexByKey(dismissedAlerts);

  return (entry: EntryFromSkeleton<TypeAlertSkeleton>): boolean => {
    // Create the comparison key for the entry
    let key = getComparisonKey({
      id: entry.sys.id,
      origin,
    });

    let wasNotDismissed = !(key in groupedByDate);

    if (typeof entry.fields.resetAt === 'undefined') {
      return wasNotDismissed;
    }

    let resetAt = DateTime.fromISO(entry.fields.resetAt).toLocal();

    if (!resetAt.isValid) {
      return wasNotDismissed;
    }

    return shouldBeRendered(resetAt, groupedByDate[key]);
  };
};

/**
 * Creates a filter function that checks if the provided alert is dismissed based on the collection of reset dates.
 */
export const createClientAlertFilter = (args: {
  resetCollection: Record<string, string>;
}) => {
  let { resetCollection } = args;

  return (clientDismissedAlert: StoredDismissedAlert): boolean => {
    let wasResettet = clientDismissedAlert.id in resetCollection;

    if (!wasResettet) {
      return false;
    }

    let resetAt = DateTime.fromISO(
      resetCollection[clientDismissedAlert.id],
    ).toLocal();

    if (!resetAt.isValid) {
      return false;
    }

    return shouldBeRendered(resetAt, clientDismissedAlert.dismissedAt);
  };
};

/**
 * Indicates whether the alert should be rendered based on the reset and dismissed dates.
 */
const shouldBeRendered = (
  resetAt: DateTime<true> | undefined,
  dismissedAt: DateTime<true> | undefined,
): boolean => {
  // If no dismissedAt date is provided, the alert should be rendered
  if (typeof dismissedAt === 'undefined') {
    return true;
  }

  // If no resetAt date is provided, the alert should not be rendered, because it is not reset and has set a dismissed date.
  if (typeof resetAt === 'undefined') {
    return false;
  }

  let now = DateTime.now();

  // If the reset date is in the future, the alert should not be rendered -- future reset date would lead to alerts which cannot be dismissed.
  if (resetAt > now) {
    return false;
  }

  // If the dismissed date is before the reset date, the alert should be rendered.
  return dismissedAt < resetAt;
};
